// Tiny tones made with the Web Audio API, so there are no sound files to download or precache.
// Every call is a silent no-op when sound is off, unsupported or still locked, so a missing
// sound can never break a tap.

const PREF_KEY = "pointz.sound";

type Ctor = typeof AudioContext;

let ctx: AudioContext | null = null;

/** Sound is on unless this device turned it off. Default on. */
export function isSoundOn(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== "off";
  } catch {
    return true;
  }
}

export function setSoundOn(on: boolean): void {
  try {
    if (on) localStorage.removeItem(PREF_KEY);
    else localStorage.setItem(PREF_KEY, "off");
  } catch {
    /* storage blocked: the choice lasts until the page closes, which is the best on offer */
  }
}

function audio(): AudioContext | null {
  if (!ctx) {
    const Ctx: Ctor | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
    if (!Ctx) return null;
    try {
      ctx = new Ctx();
    } catch {
      return null;
    }
  }
  // iOS also reports "interrupted" (a call, Siri, the app sent to the background), not only
  // "suspended", so anything short of running is woken.
  if (ctx.state !== "running") void ctx.resume().catch(() => undefined);
  return ctx;
}

/**
 * iOS only lets audio start inside a user gesture, and a minus tap lands at the end of a hold
 * (a timer, not a gesture). So the context is created, or woken, on the first touch or key
 * press anywhere, and every later tone can play whenever it is asked for. iOS suspends it again
 * when the app goes to the background, so coming back re-arms the unlock for the next touch.
 */
export function unlockSoundOnGesture(): void {
  const arm = () => {
    window.addEventListener("pointerdown", unlock, true);
    window.addEventListener("keydown", unlock, true);
  };
  const unlock = () => {
    if (!isSoundOn()) return;
    const c = audio();
    if (!c) return;
    // A silent blip inside the gesture is what finally unmutes older iOS Safari.
    tone(c, 440, 0, 0.01, 0.0001);
    if (c.state === "running") {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    }
  };
  arm();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") arm();
  });
}

/** One note with a fast attack and an exponential fade, so nothing clicks. */
function tone(c: AudioContext, freq: number, delay: number, length: number, peak: number, type: OscillatorType = "sine") {
  const start = c.currentTime + delay;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + length);
  osc.connect(gain).connect(c.destination);
  osc.start(start);
  osc.stop(start + length + 0.02);
}

function play(notes: (c: AudioContext) => void): void {
  if (!isSoundOn()) return;
  try {
    const c = audio();
    if (c) notes(c);
  } catch {
    /* audio blocked: stay silent */
  }
}

/** A short, light tick: a point added, a toggle, a button that does something. */
export function playTick(): void {
  play((c) => tone(c, 1320, 0, 0.05, 0.08, "triangle"));
}

/** A soft low tone for points taken away: noticeable, never a buzzer. */
export function playMinus(): void {
  play((c) => tone(c, 196, 0, 0.22, 0.14));
}

/** A brighter two-note chime, rising a fifth, for a star. */
export function playStar(): void {
  play((c) => {
    tone(c, 880, 0, 0.3, 0.12);
    tone(c, 1318.5, 0.12, 0.45, 0.12);
  });
}
